/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Country } from '../../shared/countries/country.model';
import { City } from '../../shared/cities/city.model';
import { AllCountriesRequested } from '../../shared/countries/country.actions';
import { select, Store } from '@ngrx/store';
import { selectAllCountries } from '../../shared/countries/countries.selectors';
import { AppState } from '../../app.reducer';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-address-info',
  templateUrl: './address-info.component.html',
  styleUrls: ['./address-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressInfoComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressInfoComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressInfoComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() complete = false;
  @Input() title: string = null;
  // Labels
  @Input() labelAddress = 'Address Line 1';
  @Input() labelExtraAddress = 'Address Line 2';
  @Input() labelPostcode = 'Postcode';
  @Input() labelCountry = 'Country';
  @Input() labelCity = 'City';

  subscription: Subscription = new Subscription();
  countries$: Observable<Country[]>;

  cities: City[] = [];
  countries: Country[];

  public addressInfoForm: FormGroup = new FormGroup({
    addressLine1: new FormControl(null, [Validators.required]),
    country: new FormControl(null, [Validators.required]),
    city: new FormControl(null, [Validators.required]),
  });

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    this.store.dispatch(AllCountriesRequested());
    this.countries$ = this.store.pipe(select(selectAllCountries));

    if (this.complete) {
      this.addressInfoForm.addControl('addressLine2', new FormControl(null, []));
      this.addressInfoForm.addControl('postcode', new FormControl(null, []));
      this.addressInfoForm.controls.addressLine2.updateValueAndValidity({ emitEvent: false });
      this.addressInfoForm.controls.postcode.updateValueAndValidity({ emitEvent: false });
    }
  }

  onCountryChanged(country: Country) {
    this.addressInfoForm.patchValue({
      city: null,
    });
    if (country) {
      this.cities = country.cities;
    }
  }

  private setInitialCities(country: Country) {
    if (country) {
      const sCountry = this.countries.filter(c => c.guid === country.guid);
      if (sCountry.length > 0) {
        this.cities = sCountry[0].cities;
      }
    }
  }

  public onTouched: () => void = () => {};

  writeValue(val: any): void {
    this.subscription.add(
      this.countries$.pipe(filter(countries => countries.length !== 0)).subscribe(countries => {
        this.countries = countries;
        if (val) {
          if (typeof val.country === 'string') {
            // We need to get the city object
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const country = this.countries.filter(country => country.name.trim() === val.country.trim());
            if (country.length > 0) {
              val.country = country[0];
            } else {
              val.country = null;
            }
          }
          this.addressInfoForm.setValue(val, { emitEvent: false });
          this.setInitialCities(this.addressInfoForm.controls.country.value);
          if (typeof val.city === 'string') {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const city = this.cities.filter(city => city.name.trim() === val.city.trim());
            if (city.length === 0) {
              val.city = null;
            } else {
              val.city = city[0];
            }
          }
          this.addressInfoForm.patchValue({
            city: val.city,
          });
        }
      }),
    );
  }
  registerOnChange(fn: any): void {
    this.addressInfoForm.valueChanges.subscribe(fn);
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.addressInfoForm.disable() : this.addressInfoForm.enable();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return (
      this.addressInfoForm.invalid && {
        invalid: true,
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
